import moment from "moment-timezone";
import { useEffect } from "react";
import { CouponsQuery } from "src/api/Coupon";
import { DiscountsQuery } from "src/api/Discount";
import { BorderButton } from "src/components/buttons/BorderButton";
import { UnsavedChanges } from "src/components/forms/UnsavedChanges";
import AddOfferingHeader from "src/components/header/AddOfferingHeader";
import EnabledOfferingItem from "src/components/items/EnabledOfferingItem";
import Container from "src/components/layout/Container";
import { HighlightSection } from "src/components/layout/HighlightSection";
import AddCouponModal from "src/components/modals/AddCouponModal";
import AddDiscountModal from "src/components/modals/AddDiscountModal";
import { isClass } from "src/models/ClassModel";
import { isMembershipOffering } from "src/models/MembershipOfferingModel";
import { isOffering } from "src/models/OfferingModel";
import { useApi } from "src/stores/Api";
import { useStore } from "src/stores/Store";
import { dateFormat } from "src/utils/constants";
import formatMoney from "src/utils/formatMoney";
import { modals } from "src/utils/modals";
import tw from "src/utils/tw";
import {
  AmountType,
  coupons,
  DiscountType,
  Frequency,
} from "types/code-generator";
import {
  AddGroupOfferingStep,
  AddMembershipOfferingStep,
} from "types/offerings";

export default function AddMembershipOfferingCoupons(
  props: AddMembershipOfferingStep | AddGroupOfferingStep
) {
  const store = useStore();
  const couponsQuery = useApi<coupons>(CouponsQuery, {
    variables: {
      organizationId: store.organization.id,
      departmentId: store.organization.hasDepartments
        ? props.offering.department?.id
        : store.organization.defaultDepartment.id,
    },
  });
  const discountsQuery = useApi(DiscountsQuery, {
    variables: {
      organizationId: store.organization.id,
      departmentId: store.organization.hasDepartments
        ? props.offering.department?.id
        : store.organization.defaultDepartment.id,
    },
  });

  const availableCoupons = couponsQuery?.coupons || [];
  const availableDiscounts = discountsQuery?.discounts || [];

  useEffect(() => {
    if (couponsQuery.coupons) {
      const defaultEnabled = couponsQuery.coupons.filter(
        (c) => c.enabledByDefault
      );

      if (defaultEnabled.length > 0 && props.offering.coupons.length === 0) {
        props.updateOffering({
          coupons: defaultEnabled,
        });
      }
    }

    if (discountsQuery.discounts) {
      const defaultEnabled = discountsQuery.discounts.filter(
        (c) => c.enabledByDefault
      );

      if (
        defaultEnabled.length > 0 &&
        isClass(props.offering) &&
        props.offering.discounts.length === 0
      ) {
        props.updateOffering({
          discounts: defaultEnabled,
        });
      }
    }
  }, [couponsQuery.coupons]);

  function onNext() {
    props.onNext();
  }

  function promptAddCoupon() {
    modals.show(AddCouponModal, {
      offering: props.offering,
      coupons: availableCoupons,
      onSubmit: (coupons) => {
        props.updateOffering({
          coupons,
        });
      },
    });
  }

  function promptAddDiscount() {
    modals.show(AddDiscountModal, {
      offering: props.offering,
      onSubmit: (discounts) => {
        props.updateOffering({
          discounts,
        });
      },
    });
  }

  function removeCoupon(coupon) {
    props.updateOffering({
      coupons: props.offering.coupons.filter((c) => c.id !== coupon.id),
    });
  }

  function removeDiscount(discount) {
    if (isMembershipOffering(props.offering)) {
      return;
    }

    props.updateOffering({
      discounts: props.offering.discounts.filter((d) => d.id !== discount.id),
    });
  }

  const showDiscounts = availableDiscounts.length > 0;

  // console.log("showDiscounts", showDiscounts);

  return (
    <>
      <AddOfferingHeader
        onCancel={props.onCancel}
        onNext={onNext}
        route={props.route}
        routes={props.routes}
        isEdit={props.isEdit}
        toggleSettings={props.toggleSettings}
        visited={props.visited}
        setVisited={props.setVisited}
        disabled={couponsQuery.loading}
      />
      <Container
        main={!props.isEdit}
        large={!props.isEdit}
        full={props.isEdit}
        fullSmall={props.isEdit}
        className={tw("pb-14", !props.isEdit && "pt-24", "bg-white")}
      >
        <Container fullSmall full={props.isEdit}>
          <div className="bg-white px-4 pb-4 pt-4">
            {showDiscounts &&
              !isMembershipOffering(props.offering) &&
              !isOffering(props.offering) && (
                <>
                  <h3 className="step-subhead mb-0 pt-6">Discounts</h3>

                  <hr className="my-0 border-darkGray" />

                  <HighlightSection section="discounts">
                    <div className="px-4 py-4">
                      <div className="mb-2 font-bold">
                        {props.offering.discounts?.length} of{" "}
                        {availableDiscounts.length} discount
                        {availableDiscounts.length !== 1 && "s"} added
                      </div>
                      <div>
                        {props.offering.discounts.map((d) => {
                          const numbers = `${d.min}–${d.max}`;
                          const types =
                            d.discountType === DiscountType.MultiStudent
                              ? "participants"
                              : "offerings";
                          const amountTypes =
                            d.amountType === AmountType.Dollar
                              ? `$${formatMoney(d.amount)}`
                              : `${(d.amount * 100).toFixed(2)}%`;

                          return (
                            <EnabledOfferingItem
                              removeItem={() => removeDiscount(d)}
                              key={d.id}
                            >
                              <>
                                <strong>
                                  {numbers} {types}
                                </strong>
                                <br />
                                {amountTypes} off
                              </>
                            </EnabledOfferingItem>
                          );
                        })}
                      </div>

                      <BorderButton dashed onClick={promptAddDiscount}>
                        <i
                          aria-hidden
                          className="far fa-plus text-organization"
                        />{" "}
                        <strong>Add</strong>
                      </BorderButton>
                    </div>
                  </HighlightSection>
                </>
              )}

            <h3 className={tw("step-subhead", "mb-0", "pt-6")}>Coupons</h3>
            <hr className="my-0 border-darkGray" />

            {availableCoupons.length === 0 && (
              <div className="px-4 py-4 text-empty">No coupons found.</div>
            )}

            {availableCoupons.length > 0 && (
              <>
                <HighlightSection section="coupons">
                  <div className="px-4 py-4">
                    <div className="mb-2 font-bold">
                      {props.offering.coupons.length} of{" "}
                      {availableCoupons.length} coupon
                      {availableCoupons.length !== 1 && "s"} added
                    </div>
                    <div>
                      {props.offering.coupons
                        .filter((c) =>
                          availableCoupons.find((ac) => ac.id === c.id)
                        )
                        .map((c) => {
                          const coupon = availableCoupons.find(
                            (ac) => ac.id === c.id
                          );
                          const off =
                            coupon.amountType === AmountType.Percent
                              ? `${coupon.amount}%`
                              : `$${formatMoney(coupon.amount)}`;
                          const expiration = moment(
                            coupon.expirationDate
                          ).format(dateFormat);
                          const frequencyString =
                            coupon.frequency === Frequency.Monthly
                              ? "monthly"
                              : "one-time";

                          return (
                            <EnabledOfferingItem
                              removeItem={() => removeCoupon(coupon)}
                              key={coupon.id}
                            >
                              <>
                                <strong>{coupon.title}</strong>
                                <br />
                                Code: {coupon.code}
                                <br />
                                {off} off {frequencyString} through {expiration}
                              </>
                            </EnabledOfferingItem>
                          );
                        })}
                    </div>
                    <BorderButton dashed onClick={promptAddCoupon}>
                      <i
                        aria-hidden
                        className="far fa-plus text-organization"
                      />{" "}
                      <strong>Add</strong>
                    </BorderButton>
                  </div>
                </HighlightSection>
              </>
            )}

            <hr className="mb-8 mt-8" />

            {!props.isEdit && (
              <div>
                <BorderButton type="submit" onClick={onNext} className="mr-4">
                  Next
                </BorderButton>
                <BorderButton onClick={props.onCancel}>Cancel</BorderButton>
              </div>
            )}
          </div>
        </Container>
      </Container>

      <UnsavedChanges
        visible={props.unsavedChanges}
        onSave={onNext}
        onCancel={props.onCancel}
      />
    </>
  );
}
